'use client';

import {DateTime} from "schema-dts";
import {createContext, ReactNode, useContext, useLayoutEffect} from "react";
import {useCookies} from "react-cookie";
import {v4 as uuidv4} from "uuid";
import ReactGA from "react-ga4";

interface TrackingContextProps {
    referrer: string
    entryTime: DateTime
    entryPage: string
    anonymousId: string
}

const TrackingContext = createContext<TrackingContextProps|undefined>(undefined);

const gaMeasurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
let gaInitialized = false;

function initializeGoogleAnalytics() {
    if (
        gaInitialized ||
        typeof window === "undefined" ||
        !gaMeasurementId ||
        ReactGA.isInitialized
    ) {
        return;
    }

    ReactGA.initialize(gaMeasurementId);
    gaInitialized = true;
}

export function useTracking(): TrackingContextProps {
    const context = useContext(TrackingContext);
    if (!context) {
        throw new Error('useTracking must be used within TrackingContext');
    }
    return context;
}

interface TrackingProviderProps {
    children: ReactNode
}

export function TrackingProvider({children}: TrackingProviderProps) {
    const [cookies, setCookie] = useCookies(["eot_data", "eot_anon_id"]);

    useLayoutEffect(() => {
        initializeGoogleAnalytics();
    }, []);

    if (typeof window !== "undefined") {
        if (!cookies.eot_anon_id) {
            setCookie("eot_anon_id", uuidv4(), {
                path: "/",
                maxAge: 60*60*24*365,
                secure: true,
                sameSite: "lax",
            })
        }
        if (!cookies.eot_data) {
            setCookie("eot_data", {
                entryTime: (new Date()).valueOf(),
                entryPage: document.location.href,
                referrer: window.document.referrer
            }, {
                path: "/",
                secure: true,
                sameSite: "lax",
            })
        }
    }

    const value = {
        anonymousId: cookies.eot_anon_id,
        referrer: cookies.eot_data?.referrer,
        entryTime: cookies.eot_data?.entryTime,
        entryPage: cookies.eot_data?.entryPage,
    }
    return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>
}
